import { useCallback, useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE, unwrapPaginated } from "@/lib/pagination";

/**
 * @param {(page: number, limit: number) => Promise<{ data: unknown }>} fetcher
 * @param {unknown[]} deps - reset to page 1 when these change
 */
export function usePaginatedList(fetcher, deps = []) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetcher(page, limit);
      const p = unwrapPaginated(data);
      setItems(p.items);
      setTotal(p.total);
      setPages(p.pages);
    } catch {
      setItems([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, page, limit, ...deps]);

  useEffect(() => {
    load();
  }, [load]);

  const setLimitAndReset = (n) => {
    setLimit(n);
    setPage(1);
  };

  const resetPage = useCallback(() => setPage(1), []);

  return {
    items,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit: setLimitAndReset,
    resetPage,
    loading,
    reload: load,
  };
}
